import { useMemo, useState, useCallback } from 'react';
import { useCollaboratorsRealtimeQuery } from '@/features/Collaborators/Data/hooks/useCollaboratorsRealtimeQuery';
import { buildPaidRestRows, isOnRestNow, countPaidRestBuckets } from '../utils/vacationPaidRest';
import type { PaidRestRow, PaidRestFilterKey } from '../models/vacationPaidRest.types';

export function usePaidRestBoard() {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedSituations, setSelectedSituations] = useState<PaidRestFilterKey[]>([]);
    const [selectedRights, setSelectedRights] = useState<PaidRestFilterKey[]>([]);
    const [selectedRow, setSelectedRow] = useState<PaidRestRow | null>(null);

    const { data: collaborators, isLoading } = useCollaboratorsRealtimeQuery({
        withSalary: false,
        withResources: false,
    });

    const toggleSituation = useCallback((situation: PaidRestFilterKey) => {
        setSelectedSituations((current) =>
            current.includes(situation) ? current.filter((s) => s !== situation) : [...current, situation]
        );
    }, []);

    const toggleRight = useCallback((right: PaidRestFilterKey) => {
        setSelectedRights((current) =>
            current.includes(right) ? current.filter((r) => r !== right) : [...current, right]
        );
    }, []);

    const searchedRows = useMemo((): PaidRestRow[] => {
        const paidRestRows = buildPaidRestRows(collaborators as any);

        if (!searchQuery.trim()) return paidRestRows;

        const query = searchQuery.toLowerCase();
        return paidRestRows.filter((row) =>
            row.collaboratorName.toLowerCase().includes(query) ||
            row.cnpj?.includes(query) ||
            row.companyName?.toLowerCase().includes(query)
        );
    }, [collaborators, searchQuery]);

    const counts = useMemo(() => countPaidRestBuckets(searchedRows), [searchedRows]);

    const rows = useMemo((): PaidRestRow[] => {
        let filtered = searchedRows;

        if (selectedSituations.length > 0) {
            filtered = filtered.filter((row) => {
                const key: PaidRestFilterKey = isOnRestNow(row.paidRestPeriods ?? []) ? 'inRest' : 'offRest';
                return selectedSituations.includes(key);
            });
        }

        if (selectedRights.length > 0) {
            filtered = filtered.filter((row) => {
                const key: PaidRestFilterKey = row.hasPaidRest ? 'withRight' : 'withoutRight';
                return selectedRights.includes(key);
            });
        }

        return filtered;
    }, [searchedRows, selectedSituations, selectedRights]);

    return {
        rows,
        searchQuery,
        setSearchQuery,
        isLoading,
        selectedSituations,
        toggleSituation,
        selectedRights,
        toggleRight,
        selectedRow,
        setSelectedRow,
        counts,
    };
}
